'use client'

import { PricingValue } from '@/lib/cms/pricing'
import { WebConfig } from '@/lib/config'
import NCPopoverPanel from '@/shared/components/NCPopoverPanel'
import { ServiceIcon } from '@/shared/components/ServiceIcon'
import DropdownButton from '@/shared/components/navigation/dropdown-button'
import {
  aboutNavigationItems,
  serviceNavigationItems,
  treatmentNavigationItems,
  type ServiceNavigationItem,
} from '@/shared/components/navigation/navigation-data'
import { CloseButton, Popover, PopoverGroup } from '@headlessui/react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import type { ReactNode } from 'react'

export default function HeaderButtons() {
  return (
    <PopoverGroup className="hidden items-center gap-8 lg:flex">
      <Popover className="relative">
        {({ open }) => (
          <>
            <DropdownButton open={open}>Services</DropdownButton>
            <DropdownPanel className="w-[730px]">
              <ServicesDropdown />
            </DropdownPanel>
          </>
        )}
      </Popover>

      <Popover className="relative">
        {({ open }) => (
          <>
            <DropdownButton open={open}>About</DropdownButton>
            <DropdownPanel className="w-[570px]">
              <AboutDropdown />
            </DropdownPanel>
          </>
        )}
      </Popover>

      <Link
        href="/prices"
        className="-m-3 rounded-md p-3 text-[15px] font-medium leading-[18px] text-[#0b0b0f] transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:text-[#2563eb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2563eb]"
      >
        Pricing
      </Link>
    </PopoverGroup>
  )
}

function ServicesDropdown() {
  const services = serviceNavigationItems.filter(
    (service) =>
      WebConfig.prescriptionEnabled || service.href !== '/prescriptions-online',
  )

  return (
    <div className="flex gap-6 p-5">
      <div className="flex w-[400px] shrink-0 flex-col gap-0.5">
        <MenuSectionLabel color="orange">Services</MenuSectionLabel>
        {services.map((service) => (
          <ServiceLink key={service.href} service={service} />
        ))}
        <MenuFooterLink href="/start">See all services →</MenuFooterLink>
      </div>

      {WebConfig.treatmentsEnabled && (
        <div className="flex w-[280px] shrink-0 flex-col gap-0.5 border-l border-[#ececef] pl-6">
          <MenuSectionLabel color="teal">Treatment plans</MenuSectionLabel>
          {treatmentNavigationItems.map((treatment) => (
            <CloseButton
              as={Link}
              key={treatment.href}
              href={treatment.href}
              className="group flex items-center gap-3 rounded-[14px] px-3 py-1.5 transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#f4f4f6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2563eb]"
            >
              <Image
                src={treatment.image}
                alt=""
                className="h-[38px] w-[38px] shrink-0 rounded-[12px] object-cover object-[50%_30%]"
              />
              <span className="text-[15px] font-semibold leading-[18px] text-[#0b0b0f] transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] group-hover:text-[#2563eb]">
                {treatment.name}
              </span>
            </CloseButton>
          ))}
          <MenuFooterLink href="/start">See all plans →</MenuFooterLink>
        </div>
      )}
    </div>
  )
}

function ServiceLink({ service }: { service: ServiceNavigationItem }) {
  return (
    <CloseButton
      as={Link}
      href={service.href}
      className="group flex h-12 items-center gap-3 rounded-[14px] px-3 transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#f4f4f6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2563eb]"
    >
      <ServiceIcon
        kind={service.icon}
        className="h-8 w-8"
        iconClassName="h-3.5 w-3.5"
      />
      <span className="min-w-0 flex-1 text-left text-[15px] font-semibold leading-[18px] text-[#0b0b0f] transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] group-hover:text-[#2563eb]">
        {service.name}
        {service.isNew && (
          <span className="ml-2 rounded-full bg-[#efe7fb] px-[9px] py-0.5 text-[11px] font-semibold leading-[14px] text-[#5b3fa8]">
            New
          </span>
        )}
      </span>
      <span className="shrink-0 text-[13px] leading-4 text-[#82828b]">
        {service.pricePrefix && `${service.pricePrefix} `}
        <PricingValue name={service.priceName} />
      </span>
    </CloseButton>
  )
}

function AboutDropdown() {
  const companyItems = aboutNavigationItems.filter(
    (item) => item.section === 'company',
  )
  const helpItems = aboutNavigationItems.filter(
    (item) => item.section === 'help',
  )
  const resultsItem = aboutNavigationItems.find(
    (item) => item.section === 'featured',
  )

  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="flex gap-6">
        <AboutSection title="Company" color="blue" items={companyItems} />
        <AboutSection title="Help" color="teal" items={helpItems} divided />
      </div>

      {resultsItem && <ResultsLink item={resultsItem} />}
    </div>
  )
}

type AboutNavigationItem = (typeof aboutNavigationItems)[number]

function AboutSection({
  title,
  color,
  items,
  divided = false,
}: {
  title: string
  color: 'blue' | 'teal'
  items: AboutNavigationItem[]
  divided?: boolean
}) {
  return (
    <div
      className={clsx('flex w-[246px] shrink-0 flex-col gap-0.5', {
        'border-l border-[#ececef] pl-6': divided,
      })}
    >
      <MenuSectionLabel color={color}>{title}</MenuSectionLabel>
      {items.map((item) => (
        <CloseButton
          as={Link}
          key={item.href}
          href={item.href}
          className="flex flex-col gap-px rounded-[14px] px-3 py-2 transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#f4f4f6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2563eb]"
        >
          <span className="text-[15px] font-semibold leading-[18px] text-[#0b0b0f]">
            {item.name}
          </span>
          <span className="text-[12.5px] leading-4 text-[#82828b]">
            {item.description}
          </span>
        </CloseButton>
      ))}
    </div>
  )
}

function ResultsLink({ item }: { item: AboutNavigationItem }) {
  return (
    <CloseButton
      as={Link}
      href={item.href}
      className="group flex items-center gap-3 rounded-[14px] bg-[#dff2ec] px-3 py-2.5 transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#cfeae2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#1f8a70]"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
        <svg
          aria-hidden="true"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M3 12h4l2.5-6 4 12 2.5-6h5"
            stroke="#1f8a70"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="flex flex-col gap-px">
        <span className="text-[15px] font-semibold leading-[18px] text-[#0b0b0f]">
          {item.name}
        </span>
        <span className="text-[12.5px] leading-4 text-[#3d5a4c]">
          {item.description}
        </span>
      </span>
    </CloseButton>
  )
}

function MenuSectionLabel({
  children,
  color,
}: {
  children: ReactNode
  color: 'orange' | 'teal' | 'blue'
}) {
  return (
    <div className="flex items-center gap-2 px-3 pb-2.5 pt-1.5">
      <span
        className={clsx('h-1.5 w-1.5 shrink-0 rounded-full', {
          'bg-[#ef8e3b]': color === 'orange',
          'bg-[#43bba9]': color === 'teal',
          'bg-[#2563eb]': color === 'blue',
        })}
      />
      <span
        className={clsx(
          'text-[11px] font-semibold uppercase leading-[14px] tracking-[0.05em]',
          {
            'text-[#d97b1f]': color === 'orange',
            'text-[#1f8a70]': color === 'teal',
            'text-[#2563eb]': color === 'blue',
          },
        )}
      >
        {children}
      </span>
    </div>
  )
}

function MenuFooterLink({
  children,
  href,
}: {
  children: ReactNode
  href: string
}) {
  return (
    <div className="px-3 pb-1 pt-3">
      <CloseButton
        as={Link}
        href={href}
        className="focus-visible:outline-offset-3 rounded-sm text-[14px] font-semibold leading-[18px] text-[#2563eb] hover:text-[#174dbd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563eb]"
      >
        {children}
      </CloseButton>
    </div>
  )
}

function DropdownPanel({
  children,
  className,
}: {
  children: ReactNode
  className: string
}) {
  return (
    <NCPopoverPanel
      transition
      ignoredOutsideClickSelectors={['[data-navigation-dropdown-trigger]']}
      className={clsx(
        'absolute left-0 top-full z-50 mt-11 overflow-hidden rounded-[24px] border border-[#ececef] bg-white shadow-[0_24px_48px_rgba(11,11,15,0.1)] transition duration-[460ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] data-[closed]:-translate-y-1.5 data-[closed]:opacity-0',
        className,
      )}
    >
      {children}
    </NCPopoverPanel>
  )
}
